// src/services/pftCardFilter.js
//
// "Filter by PFT values" on the PFT Uploads page (decided 2026-10-12): staff narrow the
// extracted patient cards with rules like QueryRegX's ("RV % Predicted > 175",
// "FEV1/FVC Ratio, Post < 0.70"), then move only the patients that match.
//
//   * Each rule is { field, operator, value } over one numeric PFT value.
//   * Rules combine as "all" (every rule must match, like QueryRegX) or "any".
//   * Only the card's NEWEST PFT is checked: the same test the patient's main PFT
//     fields (and eligibility matching) use.
//   * A card without that value (not printed, unreadable, or left blank because the two
//     readings disagreed) does NOT match. A missing value is never treated as 0.
//   * A rule that isn't filled in yet (no field or no number) is ignored.
//   * Ratios (FEV1/FVC, its LLN, RV/TLC) can be typed and stored either as a fraction
//     (0.70) or a percentage (70); both sides are put on the percentage scale first.
//
// Firebase-free, so every rule is unit tested (pftCardFilter.test.js).

import { PFT_TEST_FIELDS } from "./pftTests";

export const PFT_FILTER_OPERATORS = [">", ">=", "<", "<=", "="];

// Text values on a test: never offered as a number filter.
const TEXT_KEYS = new Set([
  "referringPhysician",
  "interpretingPhysician",
  "smokingStatus",
  "bronchodilator",
  "qualityGrade",
  "referenceEquations",
  "interpretation",
]);
const isNumericField = (f) => !f.type && !f.key.endsWith("Unit") && !TEXT_KEYS.has(f.key);

const MAIN_KEYS = ["fev1fvcPre", "fev1fvcPost", "fev1Pre", "fev1Post", "fvcPre", "fvcPost", "revPercent", "revMl"];
const VOLUME_PREFIXES = ["tlc", "rv", "frc", "vc", "ic", "erv"];
const DIFFUSION_PREFIXES = ["dlco", "va", "hemoglobin"];
const startsWithAny = (key, prefixes) =>
  prefixes.some((p) => key === p || (key.startsWith(p) && /^[A-Z]/.test(key.slice(p.length))));

const NUMERIC_FIELDS = PFT_TEST_FIELDS.filter(isNumericField);
const groupOf = (key) =>
  MAIN_KEYS.includes(key)
    ? "Main PFT values"
    : startsWithAny(key, VOLUME_PREFIXES)
      ? "Lung volumes"
      : startsWithAny(key, DIFFUSION_PREFIXES)
        ? "Diffusion"
        : /^(fev1|fvc|fef2575|pef)/.test(key)
          ? "Spirometry"
          : "Other values";

/** The field picker: [{ title, fields: [{ key, label }] }], main values first. */
export const PFT_FILTER_FIELD_GROUPS = ["Main PFT values", "Lung volumes", "Diffusion", "Spirometry", "Other values"]
  .map((title) => ({
    title,
    fields: (title === "Main PFT values"
      ? MAIN_KEYS.map((k) => NUMERIC_FIELDS.find((f) => f.key === k))
      : NUMERIC_FIELDS.filter((f) => groupOf(f.key) === title)
    ).map(({ key, label }) => ({ key, label })),
  }))
  .filter((g) => g.fields.length);

export const PFT_FILTER_FIELD_KEYS = PFT_FILTER_FIELD_GROUPS.flatMap((g) => g.fields.map((f) => f.key));
export const filterFieldLabel = (key) =>
  (PFT_FILTER_FIELD_GROUPS.flatMap((g) => g.fields).find((f) => f.key === key) || {}).label || key;

// Ratios printed either way (0.68 or 68): compared on the percentage scale.
const RATIO_KEYS = new Set(["fev1fvcPre", "fev1fvcPost", "fev1fvcLlnPre", "fev1fvcLlnPost", "rvTlc"]);

/** A clean number from a stored or typed value ("68", "68 %", "1,250"), else null. */
export const toNumber = (raw) => {
  if (raw === undefined || raw === null) return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  const s = String(raw).replace(/,/g, "").replace(/%/g, "").trim();
  if (!/^[-+]?(\d+(\.\d*)?|\.\d+)$/.test(s)) return null; // "<0.5", "N/A", "" -> no number
  return Number(s);
};

const onScale = (key, n) => (n !== null && RATIO_KEYS.has(key) && Math.abs(n) <= 1.5 ? n * 100 : n);

/** A rule that is filled in: a known field, an operator and a number. */
export const isActiveRule = (r) =>
  !!r && PFT_FILTER_FIELD_KEYS.includes(r.field) && PFT_FILTER_OPERATORS.includes(r.operator) && toNumber(r.value) !== null;

const compare = (a, op, b) => {
  const eq = Math.abs(a - b) < 1e-9;
  if (op === ">") return a > b && !eq;
  if (op === ">=") return a > b || eq;
  if (op === "<") return a < b && !eq;
  if (op === "<=") return a < b || eq;
  return eq;
};

/** Does one test satisfy one (active) rule? A missing value never matches. */
export const testMatchesRule = (test, rule) => {
  const have = onScale(rule.field, toNumber(test ? test[rule.field] : null));
  if (have === null) return false;
  return compare(have, rule.operator, onScale(rule.field, toNumber(rule.value)));
};

/** The test the filters check: the card's newest (cards list tests newest first). */
export const newestTestOf = (card) => (card && Array.isArray(card.tests) && card.tests[0]) || null;

/**
 * Does a card match? mode "all" (every rule) or "any" (at least one).
 * With no active rules, every card matches (no filter).
 */
export const cardMatchesFilters = (card, rules = [], mode = "all") => {
  const active = rules.filter(isActiveRule);
  if (!active.length) return true;
  const test = newestTestOf(card);
  if (!test) return false;
  return mode === "any" ? active.some((r) => testMatchesRule(test, r)) : active.every((r) => testMatchesRule(test, r));
};

/** True when at least one rule is filled in. */
export const hasActiveFilters = (rules = []) => rules.some(isActiveRule);

/**
 * What "Move filtered" moves, from the matching cards:
 *   move     ready (nothing left to check) and not left out by staff
 *   review   skipped: still have unticked review items (decided 2026-10-12)
 *   leftOut  unticked by staff
 */
export const planFilteredMove = (matching = [], excludedKeys = new Set()) => {
  const out = { move: [], review: [], leftOut: [] };
  for (const c of matching) {
    if (!c || c.status !== "new") continue;
    if (excludedKeys.has(c.key)) out.leftOut.push(c);
    else if (c.needsReview) out.review.push(c);
    else out.move.push(c);
  }
  return out;
};
